/**
 * Socket.IO Provider for Admin Panel
 *
 * Provides real-time WebSocket connectivity for admin collaboration features:
 * - Content Control (Home Layout Manager) - presence, publish notifications
 * - Store Command Center - live metrics updates
 *
 * Uses JWT authentication and room-based subscriptions.
 *
 * @module providers/SocketIOProvider
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { io, Socket } from 'socket.io-client';
import Logger from '../utils/Logger';
import config from '../config';
import { getValidToken } from '../services/tokenRefreshService';

// ============================================================================
// Types
// ============================================================================

interface AdminUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
}

interface PresenceUpdate {
  userId: string;
  userName: string;
  avatarUrl?: string | null;
  viewingSection: string | null;
  editingItemId: string | null;
  timestamp: string;
}

interface HomeLayoutPublishedEvent {
  type: 'HomeLayoutPublished';
  categoriesModified: number;
  bannersModified: number;
  publishedBy: AdminUser;
  timestamp: string;
}

interface StoreMetricsUpdateEvent {
  type: 'admin:store-metrics-update';
  storeId: string;
  storeName: string;
  signals: {
    inTransit: number;
    packing: number;
    awaitingPickup: number;
    alerts: number;
  };
  timestamp: string;
}

// Picking real-time events (from companion app via backend broadcasts)
export interface PickingProgressInfo {
  total: number;
  found: number;
  notFound: number;
  pending: number;
}

export interface PickingStartedEvent {
  type: 'PickingStarted';
  orderId: string;
  orderNumber: string;
  picker: { id: string; name: string };
  startedAt: string;
  timestamp: string;
}

export interface PickingItemUpdatedEvent {
  type: 'PickingItemUpdated';
  orderId: string;
  orderNumber: string;
  lineId: string;
  productName: string;
  productSku: string;
  pickingStatus: 'found' | 'not_found';
  pickedQuantity: number;
  progress: PickingProgressInfo;
  timestamp: string;
}

export interface PickingItemNotFoundEvent {
  type: 'PickingItemNotFound';
  orderId: string;
  orderNumber: string;
  lineId: string;
  productName: string;
  productSku: string;
  pickerName: string;
  timestamp: string;
}

export interface PickingCompletedEvent {
  type: 'PickingCompleted';
  orderId: string;
  orderNumber: string;
  pickerName: string;
  hasNotFoundItems: boolean;
  progress: PickingProgressInfo;
  timestamp: string;
}

/** An order's status changed (any source). Sent to the store's admin room; reload the order. */
export interface OrderStatusChangedEvent {
  orderId: string;
  orderNumber: string;
  status: string;
  deliveryStatus?: string;
  timestamp: string;
}

export interface ManagerAlertCreatedEvent {
  type: 'ManagerAlertCreated';
  alert: {
    id: string;
    storeId: string;
    orderId: string;
    alertType: string;
    message: string;
    isDismissed: boolean;
    createdAt: string;
  };
  storeId: string;
  timestamp: string;
}

export interface ChatMessageEvent {
  type: 'ChatMessage';
  orderId: string;
  orderNumber: string;
  sender: { id: string; name: string; role: string };
  messageType: string;
  content: string | null;
  photoUrl: string | null;
  timestamp: string;
}

// SKC Processing Progress events
export interface SkcProcessingStepInfo {
  name: string;
  status: 'pending' | 'running' | 'done' | 'skipped';
  startedAt?: string;
  completedAt?: string;
  detail?: string;
}

export interface VaperiteProcessingProgressEvent {
  type: string;
  /** 'vaperite' | 'deeliver': which item-staging pipeline the run belongs to (absent from older servers). */
  org?: string;
  payloadId: string;
  storeName: string | null;
  progress: {
    currentStep: string;
    stepIndex: number;
    totalSteps: number;
    itemsProcessed: number;
    totalItems: number;
    steps: SkcProcessingStepInfo[];
    startedAt: string;
    completedAt?: string;
    error?: string;
    result?: {
      processedImmediately: number;
      stagedForApproval: number;
      errors: number;
    };
  };
  timestamp: string;
}

// Uber Orders events
export interface UberNewOrderEvent {
  id: string;
  uberOrderId: string;
  displayId: string;
  status: string;
  customerName: string;
  customerPhone: string;
  items: Array<{
    sku: string;
    name: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  totalAmount: number;
  deliveryFee: number;
  placedAt: string;
  legacySaleId?: string;
  legacyOrderNumber?: string;
  createdAt: string;
}

interface SocketIOContextValue {
  /** Socket.IO connection status */
  isConnected: boolean;

  /** Last connection error (if any) */
  connectionError: string | null;

  /** Subscribe to admin:home-layout room */
  subscribeHomeLayout: () => void;

  /** Unsubscribe from admin:home-layout room */
  unsubscribeHomeLayout: () => void;

  /** Subscribe to admin:command-center room */
  subscribeCommandCenter: () => void;

  /** Unsubscribe from admin:command-center room */
  unsubscribeCommandCenter: () => void;

  /** Update presence (what section you're viewing/editing) */
  updatePresence: (viewingSection: string | null, editingItemId?: string | null) => void;

  /** Register callback for presence updates */
  onPresenceUpdate: (callback: (presence: PresenceUpdate[]) => void) => () => void;

  /** Register callback for home layout published events */
  onHomeLayoutPublished: (callback: (event: HomeLayoutPublishedEvent) => void) => () => void;

  /** Register callback for user joined events */
  onUserJoined: (callback: (user: AdminUser) => void) => () => void;

  /** Register callback for user left events */
  onUserLeft: (callback: (userId: string) => void) => () => void;

  /** Register callback for store metrics updates (Command Center) */
  onStoreMetricsUpdate: (callback: (event: StoreMetricsUpdateEvent) => void) => () => void;

  /** Subscribe to a store's admin room for picking events */
  subscribeStore: (storeId: string) => void;

  /** Unsubscribe from a store's admin room */
  unsubscribeStore: (storeId: string) => void;

  /** Register callback for picking started events */
  onPickingStarted: (callback: (event: PickingStartedEvent) => void) => () => void;

  /** Register callback for picking item updated events */
  onPickingItemUpdated: (callback: (event: PickingItemUpdatedEvent) => void) => () => void;

  /** Register callback for picking item not found events */
  onPickingItemNotFound: (callback: (event: PickingItemNotFoundEvent) => void) => () => void;

  /** Register callback for picking completed events */
  onPickingCompleted: (callback: (event: PickingCompletedEvent) => void) => () => void;

  /** Register callback for order status changes in a subscribed store */
  onOrderStatusChanged: (callback: (event: OrderStatusChangedEvent) => void) => () => void;

  /** Register callback for manager alert created events */
  onManagerAlertCreated: (callback: (event: ManagerAlertCreatedEvent) => void) => () => void;

  /** Register callback for chat message events */
  onChatMessage: (callback: (event: ChatMessageEvent) => void) => () => void;

  /** Subscribe to admin:vaperite-processing room */
  subscribeSkcProcessing: () => void;

  /** Unsubscribe from admin:vaperite-processing room */
  unsubscribeSkcProcessing: () => void;

  /** Register callback for SKC processing progress events */
  onVaperiteProcessingProgress: (callback: (event: VaperiteProcessingProgressEvent) => void) => () => void;

  /** Subscribe to admin:uber-orders room */
  subscribeUberOrders: () => void;

  /** Unsubscribe from admin:uber-orders room */
  unsubscribeUberOrders: () => void;

  /** Register callback for new Uber order events */
  onUberNewOrder: (callback: (event: UberNewOrderEvent) => void) => () => void;

  /** Subscribe to all store rooms for new order alerts */
  subscribeAllOrders: (storeIds: string[]) => void;

  /** Register callback for new order events (from any subscribed store) */
  onNewOrder: (callback: (event: any) => void) => () => void;
}

const SocketIOContext = createContext<SocketIOContextValue | undefined>(undefined);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get WebSocket URL from API URL
 * Converts https://api.example.com/v1 to wss://api.example.com
 */
function getSocketUrl(): string {
  const apiUrl = config.API_URL;

  // Remove /v1 suffix if present
  const baseUrl = apiUrl.replace(/\/v1\/?$/, '');

  // Convert http(s) to ws(s)
  return baseUrl.replace(/^http/, 'ws');
}

/**
 * Longest wait between reconnect attempts after the server refuses the handshake.
 * socket.io retries transport failures itself, but NOT a rejection from the auth
 * middleware — that one we have to retry by hand.
 */
const AUTH_RETRY_MAX_MS = 30000;

// ============================================================================
// Provider Component
// ============================================================================

interface SocketIOProviderProps {
  children: React.ReactNode;
}

export function SocketIOProvider({ children }: SocketIOProviderProps): React.ReactElement {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Socket instance ref (persists across renders)
  const socketRef = useRef<Socket | null>(null);

  // Callback refs for event listeners
  const presenceCallbacksRef = useRef<Set<(presence: PresenceUpdate[]) => void>>(new Set());
  const publishedCallbacksRef = useRef<Set<(event: HomeLayoutPublishedEvent) => void>>(new Set());
  const userJoinedCallbacksRef = useRef<Set<(user: AdminUser) => void>>(new Set());
  const userLeftCallbacksRef = useRef<Set<(userId: string) => void>>(new Set());
  const storeMetricsCallbacksRef = useRef<Set<(event: StoreMetricsUpdateEvent) => void>>(new Set());

  // Picking event callback refs
  const pickingStartedCallbacksRef = useRef<Set<(event: PickingStartedEvent) => void>>(new Set());
  const pickingItemUpdatedCallbacksRef = useRef<Set<(event: PickingItemUpdatedEvent) => void>>(new Set());
  const pickingItemNotFoundCallbacksRef = useRef<Set<(event: PickingItemNotFoundEvent) => void>>(new Set());
  const pickingCompletedCallbacksRef = useRef<Set<(event: PickingCompletedEvent) => void>>(new Set());
  const orderStatusChangedCallbacksRef = useRef<Set<(event: OrderStatusChangedEvent) => void>>(new Set());
  const managerAlertCreatedCallbacksRef = useRef<Set<(event: ManagerAlertCreatedEvent) => void>>(new Set());
  const chatMessageCallbacksRef = useRef<Set<(event: ChatMessageEvent) => void>>(new Set());
  const vaperiteProcessingCallbacksRef = useRef<Set<(event: VaperiteProcessingProgressEvent) => void>>(new Set());
  const uberNewOrderCallbacksRef = useRef<Set<(event: UberNewOrderEvent) => void>>(new Set());
  const newOrderCallbacksRef = useRef<Set<(event: any) => void>>(new Set());

  // Initialize socket connection
  useEffect(() => {
    const socketUrl = getSocketUrl();
    Logger.info('[SocketIO] Connecting to:', socketUrl);

    // `auth` as a function is re-evaluated on EVERY (re)connect, so a reconnect after
    // a deploy or network drop carries a fresh token. The old version read a static
    // token once, on mount, from localStorage 'auth' — a key nothing writes (login
    // stores 'token'), so the dash either skipped the socket or sent a months-old token.
    const socket = io(socketUrl, {
      auth: (cb) => {
        getValidToken()
          .then((token) => cb({ token: token ?? '' }))
          .catch(() => cb({ token: '' }));
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    socketRef.current = socket;

    let authRetryTimer: ReturnType<typeof setTimeout> | null = null;
    let authRetries = 0;

    // Connection events
    socket.on('connect', () => {
      Logger.info('[SocketIO] Connected successfully');
      authRetries = 0;
      setIsConnected(true);
      setConnectionError(null);
    });

    socket.on('disconnect', (reason) => {
      Logger.info('[SocketIO] Disconnected:', reason);
      setIsConnected(false);
      // The one disconnect socket.io will not recover from on its own.
      if (reason === 'io server disconnect') socket.connect();
    });

    socket.on('connect_error', (error) => {
      Logger.error('[SocketIO] Connection error:', error.message);
      setConnectionError(error.message);
      setIsConnected(false);

      // `socket.active` is false when the server's auth middleware refused us (no
      // token yet, expired token, not logged in). socket.io gives up on that case, so
      // retry by hand with backoff — the auth callback fetches a fresh token each time.
      if (!socket.active) {
        const delay = Math.min(AUTH_RETRY_MAX_MS, 2000 * 2 ** authRetries);
        authRetries += 1;
        if (authRetryTimer) clearTimeout(authRetryTimer);
        authRetryTimer = setTimeout(() => socket.connect(), delay);
      }
    });

    // Admin collaboration events
    socket.on('admin:presence-update', (data: { activeUsers: PresenceUpdate[] }) => {
      presenceCallbacksRef.current.forEach((cb) => cb(data.activeUsers));
    });

    socket.on('admin:layout-published', (data: HomeLayoutPublishedEvent) => {
      publishedCallbacksRef.current.forEach((cb) => cb(data));
    });

    socket.on('admin:user-joined', (data: AdminUser) => {
      userJoinedCallbacksRef.current.forEach((cb) => cb(data));
    });

    socket.on('admin:user-left', (data: { userId: string }) => {
      userLeftCallbacksRef.current.forEach((cb) => cb(data.userId));
    });

    socket.on('admin:store-metrics-update', (data: StoreMetricsUpdateEvent) => {
      storeMetricsCallbacksRef.current.forEach((cb) => cb(data));
    });

    // Picking events (from companion app via backend)
    socket.on('picking:started', (data: PickingStartedEvent) => {
      pickingStartedCallbacksRef.current.forEach((cb) => cb(data));
    });

    socket.on('picking:item-updated', (data: PickingItemUpdatedEvent) => {
      pickingItemUpdatedCallbacksRef.current.forEach((cb) => cb(data));
    });

    socket.on('picking:item-not-found', (data: PickingItemNotFoundEvent) => {
      pickingItemNotFoundCallbacksRef.current.forEach((cb) => cb(data));
    });

    socket.on('picking:completed', (data: PickingCompletedEvent) => {
      pickingCompletedCallbacksRef.current.forEach((cb) => cb(data));
    });

    socket.on('order:status-changed', (data: OrderStatusChangedEvent) => {
      orderStatusChangedCallbacksRef.current.forEach((cb) => cb(data));
    });

    // Manager alert events
    socket.on('ManagerAlertCreated', (data: ManagerAlertCreatedEvent) => {
      managerAlertCreatedCallbacksRef.current.forEach((cb) => cb(data));
    });

    // Chat message events (from CometChat webhook via backend)
    socket.on('chat:message', (data: ChatMessageEvent) => {
      chatMessageCallbacksRef.current.forEach((cb) => cb(data));
    });

    // SKC processing progress events
    socket.on('vaperite:processing-progress', (data: VaperiteProcessingProgressEvent) => {
      vaperiteProcessingCallbacksRef.current.forEach((cb) => cb(data));
    });

    // New order events (from store admin rooms)
    socket.on('order:new', (data: any) => {
      newOrderCallbacksRef.current.forEach((cb) => cb(data));
    });

    // Uber order events
    socket.on('uber:new-order', (data: UberNewOrderEvent) => {
      uberNewOrderCallbacksRef.current.forEach((cb) => cb(data));
    });

    // Cleanup on unmount
    return () => {
      Logger.info('[SocketIO] Cleaning up connection');
      if (authRetryTimer) clearTimeout(authRetryTimer);
      socket.disconnect();
      socketRef.current = null;
    };
  }, []); // Only run once on mount

  // React to a login/logout in another tab ('storage' does not fire in the tab that
  // wrote the key — this tab's own login is picked up by the auth retry above).
  // A mere token refresh needs nothing: an open socket stays authenticated.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== 'token') return;
      const socket = socketRef.current;
      if (!socket) return;
      if (!e.newValue) {
        socket.disconnect();
      } else if (!socket.connected) {
        socket.connect();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Room subscription functions
  const subscribeHomeLayout = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:subscribe', { room: 'home-layout' });
      Logger.info('[SocketIO] Subscribed to admin:home-layout');
    }
  }, []);

  const unsubscribeHomeLayout = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:unsubscribe', { room: 'home-layout' });
      Logger.info('[SocketIO] Unsubscribed from admin:home-layout');
    }
  }, []);

  const subscribeCommandCenter = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:subscribe', { room: 'command-center' });
      Logger.info('[SocketIO] Subscribed to admin:command-center');
    }
  }, []);

  const unsubscribeCommandCenter = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:unsubscribe', { room: 'command-center' });
      Logger.info('[SocketIO] Unsubscribed from admin:command-center');
    }
  }, []);

  // Presence update function
  const updatePresence = useCallback((viewingSection: string | null, editingItemId: string | null = null) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:presence', {
        viewingSection,
        editingItemId,
      });
    }
  }, []);

  // Event listener registration functions
  const onPresenceUpdate = useCallback((callback: (presence: PresenceUpdate[]) => void) => {
    presenceCallbacksRef.current.add(callback);
    return () => {
      presenceCallbacksRef.current.delete(callback);
    };
  }, []);

  const onHomeLayoutPublished = useCallback((callback: (event: HomeLayoutPublishedEvent) => void) => {
    publishedCallbacksRef.current.add(callback);
    return () => {
      publishedCallbacksRef.current.delete(callback);
    };
  }, []);

  const onUserJoined = useCallback((callback: (user: AdminUser) => void) => {
    userJoinedCallbacksRef.current.add(callback);
    return () => {
      userJoinedCallbacksRef.current.delete(callback);
    };
  }, []);

  const onUserLeft = useCallback((callback: (userId: string) => void) => {
    userLeftCallbacksRef.current.add(callback);
    return () => {
      userLeftCallbacksRef.current.delete(callback);
    };
  }, []);

  const onStoreMetricsUpdate = useCallback((callback: (event: StoreMetricsUpdateEvent) => void) => {
    storeMetricsCallbacksRef.current.add(callback);
    return () => {
      storeMetricsCallbacksRef.current.delete(callback);
    };
  }, []);

  // Store admin room subscriptions (for picking events)
  const subscribeStore = useCallback((storeId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:subscribe-store', { storeId });
      Logger.info(`[SocketIO] Subscribed to admin:${storeId} (picking events)`);
    }
  }, []);

  const unsubscribeStore = useCallback((storeId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:unsubscribe-store', { storeId });
      Logger.info(`[SocketIO] Unsubscribed from admin:${storeId}`);
    }
  }, []);

  // Picking event listener registration
  const onPickingStarted = useCallback((callback: (event: PickingStartedEvent) => void) => {
    pickingStartedCallbacksRef.current.add(callback);
    return () => {
      pickingStartedCallbacksRef.current.delete(callback);
    };
  }, []);

  const onPickingItemUpdated = useCallback((callback: (event: PickingItemUpdatedEvent) => void) => {
    pickingItemUpdatedCallbacksRef.current.add(callback);
    return () => {
      pickingItemUpdatedCallbacksRef.current.delete(callback);
    };
  }, []);

  const onPickingItemNotFound = useCallback((callback: (event: PickingItemNotFoundEvent) => void) => {
    pickingItemNotFoundCallbacksRef.current.add(callback);
    return () => {
      pickingItemNotFoundCallbacksRef.current.delete(callback);
    };
  }, []);

  const onPickingCompleted = useCallback((callback: (event: PickingCompletedEvent) => void) => {
    pickingCompletedCallbacksRef.current.add(callback);
    return () => {
      pickingCompletedCallbacksRef.current.delete(callback);
    };
  }, []);

  const onOrderStatusChanged = useCallback((callback: (event: OrderStatusChangedEvent) => void) => {
    orderStatusChangedCallbacksRef.current.add(callback);
    return () => {
      orderStatusChangedCallbacksRef.current.delete(callback);
    };
  }, []);

  const onManagerAlertCreated = useCallback((callback: (event: ManagerAlertCreatedEvent) => void) => {
    managerAlertCreatedCallbacksRef.current.add(callback);
    return () => {
      managerAlertCreatedCallbacksRef.current.delete(callback);
    };
  }, []);

  const onChatMessage = useCallback((callback: (event: ChatMessageEvent) => void) => {
    chatMessageCallbacksRef.current.add(callback);
    return () => {
      chatMessageCallbacksRef.current.delete(callback);
    };
  }, []);

  // SKC Processing subscriptions
  const subscribeSkcProcessing = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:subscribe', { room: 'vaperite-processing' });
      Logger.info('[SocketIO] Subscribed to admin:vaperite-processing');
    }
  }, []);

  const unsubscribeSkcProcessing = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:unsubscribe', { room: 'vaperite-processing' });
      Logger.info('[SocketIO] Unsubscribed from admin:vaperite-processing');
    }
  }, []);

  const onVaperiteProcessingProgress = useCallback((callback: (event: VaperiteProcessingProgressEvent) => void) => {
    vaperiteProcessingCallbacksRef.current.add(callback);
    return () => {
      vaperiteProcessingCallbacksRef.current.delete(callback);
    };
  }, []);

  // Uber Orders subscriptions
  const subscribeUberOrders = useCallback(() => {
    if (socketRef.current?.connected) {
      // Read the server's ack — a refused subscribe used to log "Subscribed" and then
      // silently receive nothing, which is how the missing room went unnoticed.
      socketRef.current.emit('admin:subscribe', { room: 'uber-orders' }, (ack?: { success: boolean; error?: string }) => {
        if (ack && !ack.success) {
          Logger.warn('[SocketIO] Refused admin:uber-orders subscription:', ack.error);
          return;
        }
        Logger.info('[SocketIO] Subscribed to admin:uber-orders');
      });
    }
  }, []);

  const unsubscribeUberOrders = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin:unsubscribe', { room: 'uber-orders' });
      Logger.info('[SocketIO] Unsubscribed from admin:uber-orders');
    }
  }, []);

  const onUberNewOrder = useCallback((callback: (event: UberNewOrderEvent) => void) => {
    uberNewOrderCallbacksRef.current.add(callback);
    return () => {
      uberNewOrderCallbacksRef.current.delete(callback);
    };
  }, []);

  // New order subscriptions (subscribe to all store rooms for order alerts)
  const subscribeAllOrders = useCallback((storeIds: string[]) => {
    if (!socketRef.current?.connected) return;
    for (const storeId of storeIds) {
      socketRef.current.emit('admin:subscribe-store', { storeId });
    }
    Logger.info(`[SocketIO] Subscribed to ${storeIds.length} store rooms for order alerts`);
  }, []);

  const onNewOrder = useCallback((callback: (event: any) => void) => {
    newOrderCallbacksRef.current.add(callback);
    return () => {
      newOrderCallbacksRef.current.delete(callback);
    };
  }, []);

  const contextValue: SocketIOContextValue = {
    isConnected,
    connectionError,
    subscribeHomeLayout,
    unsubscribeHomeLayout,
    subscribeCommandCenter,
    unsubscribeCommandCenter,
    updatePresence,
    onPresenceUpdate,
    onHomeLayoutPublished,
    onUserJoined,
    onUserLeft,
    onStoreMetricsUpdate,
    subscribeStore,
    unsubscribeStore,
    onPickingStarted,
    onPickingItemUpdated,
    onPickingItemNotFound,
    onPickingCompleted,
    onOrderStatusChanged,
    onManagerAlertCreated,
    onChatMessage,
    subscribeSkcProcessing,
    unsubscribeSkcProcessing,
    onVaperiteProcessingProgress,
    subscribeUberOrders,
    unsubscribeUberOrders,
    onUberNewOrder,
    subscribeAllOrders,
    onNewOrder,
  };

  return (
    <SocketIOContext.Provider value={contextValue}>
      {children}
    </SocketIOContext.Provider>
  );
}

/**
 * Hook to access the Socket.IO context
 *
 * @returns SocketIO context value
 * @throws Error if used outside of SocketIOProvider
 */
export function useSocketIO(): SocketIOContextValue {
  const context = useContext(SocketIOContext);
  if (context === undefined) {
    throw new Error('useSocketIO must be used within a SocketIOProvider');
  }
  return context;
}

export default SocketIOContext;
